import { Component } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { copyToClipboard, docsCallExamples, DocsLang, highlightJson, stripHighlighting } from '../utils/docs-code';

/** The representative call used for the Quickstart's example a real,
 *  valid /rag call (not the illustrative "completions" field a draft
 *  mockup showed, which isn't an actual parameter see
 *  data/docs-api-data.ts's real rag param table). */
const QUICKSTART_EXAMPLE = {
  request: {
    question: 'What are the key announcements of Skanska for last 6 months?',
    include_domains: ['skanska.com'],
    exclude_domains: ['google.com'],
  },
  response: { comprehensiveness: 10 },
};

/** What that call actually comes back with shaped from the real
 *  rag_answer/snippet schema (data/docs-api-data.ts's DOCS_SCHEMAS, the
 *  documented customer-facing contract), not the Playground's own
 *  Snippet/Item models, which that file's own header comment says
 *  predate this contract and don't match it yet. Wrapped in the same
 *  { success, messages, data } envelope documented on the Making a call
 *  page. Only the fields every snippet always carries are shown
 *  (snippet, url, published_at) relevance_score/domain_name/etc. each
 *  need their own include_* flag, none of which this minimal call sets,
 *  per the rag endpoint's own summary. */
const QUICKSTART_RESPONSE_EXAMPLE = {
  success: true,
  messages: [],
  data: {
    question: 'What are the key announcements of Skanska for last 6 months?',
    snippets: [
      {
        snippet: "Skanska has been awarded a $420 million contract to build a new highway interchange, its largest infrastructure win of the year.",
        url: 'https://www.skanska.com/newsroom/press-releases/2026/skanska-wins-contract',
        published_at: '2026-05-12T09:00:00Z',
      },
      {
        snippet: 'The company also reported a 12% rise in quarterly revenue, driven largely by its civil engineering and residential construction divisions.',
        url: 'https://www.skanska.com/investors/reports/q2-2026-earnings',
        published_at: '2026-04-22T14:30:00Z',
      },
      {
        snippet: 'Skanska announced a new sustainability target: all new commercial projects to be net-zero carbon in operation by 2028.',
        url: 'https://www.skanska.com/newsroom/press-releases/2026/net-zero-2028',
        published_at: '2026-03-05T11:15:00Z',
      },
    ],
    meta: { count: 3, most_frequent_answer: null },
  },
};

/**
 * Quickstart guide (/docs/quickstart) the three-step "get a key, send a
 * request, read the response" walkthrough with its own runnable example.
 * Split out of the old single /docs mega-page (see docs/overview/docs.ts)
 * so it can be linked, searched and (per the sidebar's "Get started"
 * section) navigated to directly.
 */
@Component({
  selector: 'app-docs-quickstart',
  standalone: false,
  templateUrl: './docs-quickstart.html',
  styleUrl: './docs-quickstart.scss',
})
export class DocsQuickstart {
  readonly languages: { id: DocsLang; label: string }[] = [
    { id: 'curl', label: 'cURL' },
    { id: 'javascript', label: 'JavaScript' },
    { id: 'python', label: 'Python' },
  ];

  private lang: DocsLang = 'curl';
  copied = false;

  constructor(private sanitizer: DomSanitizer) {}

  setLang(lang: DocsLang): void {
    this.lang = lang;
  }

  activeLang(): DocsLang {
    return this.lang;
  }

  get callExampleHtml(): SafeHtml {
    const examples = docsCallExamples('/api/rag', QUICKSTART_EXAMPLE);
    return this.sanitizer.bypassSecurityTrustHtml(examples[this.lang]);
  }

  get responseExampleHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(highlightJson(QUICKSTART_RESPONSE_EXAMPLE));
  }

  async copyCallExample(): Promise<void> {
    const examples = docsCallExamples('/api/rag', QUICKSTART_EXAMPLE);
    if (!(await copyToClipboard(stripHighlighting(examples[this.lang])))) return;
    this.copied = true;
    setTimeout(() => { this.copied = false; }, 2000);
  }
}
